import { ColumnState } from 'ag-grid-community';
import { DateRange } from 'react-day-picker';
import { create } from 'zustand';
import { PRD3031_R01Dto, PRD3031_R01GridRowData, PRD3031_R01SubtotalData, PRD3031_R01DateSubtotalData } from './Dto_PRD3031_R01';
import { getCurrentMonthDateRange } from 'src/utils/date/dateUtils';

export interface PRD3031_R01Filter {
    compCd: string;
    productDateRange: DateRange | undefined;
    orderType: string;
    workplaceCd: string;
    itemCd: string;
    itemNm: string;
    brandCd: string;
}

export interface ExtendedPRD3031_R01Dto extends PRD3031_R01Dto {
    isSubtotal: false;
}

/**
 * Zustand 스토어의 상태 및 액션 정의
 */
export interface Store_PRD3031_R01 {
    PRD3031_R01Filter: PRD3031_R01Filter;
    PRD3031_R01PastFilter: PRD3031_R01Filter;
    setPRD3031_R01Filter: (filter: Partial<PRD3031_R01Filter>) => void;
    setPRD3031_R01PastFilter: (filter: Partial<PRD3031_R01Filter>) => void;

    // === 리스트 관련 ===
    PRD3031_R01List: ExtendedPRD3031_R01Dto[];
    setPRD3031_R01List: (PRD3031_R01List: ExtendedPRD3031_R01Dto[]) => void;
    insertPRD3031_R01List: (data: ExtendedPRD3031_R01Dto[], index?: number) => void;
    removePRD3031_R01List: (index: number) => void;
    updatePRD3031_R01List: (data: ExtendedPRD3031_R01Dto, index: number) => void;

    // === 선택 관련 ===
    selectedPRD3031_R01List: string[];
    setSelectedPRD3031_R01List: (idList: string[]) => void;
    addSelectedPRD3031_R01List: (idList: string[]) => void;
    removeSelectedPRD3031_R01List: (idList?: string[]) => void;
    initSelectedPRD3031_R01List: () => void;

    // === AG Grid 컬럼 상태 관리 ===
    columnState: ColumnState[] | null;
    setColumnState: (state: ColumnState[] | null) => void;

    scrollTop: number | null;
    setScrollTop: (top: number | null) => void;

    allCount: number;
    setAllCount: (allCount: number) => void;

    // === 통합 소계 관련 ===
    showSubtotals: boolean;
    setShowSubtotals: (show: boolean) => void;
    subtotalData: PRD3031_R01SubtotalData[];
    setSubtotalData: (data: PRD3031_R01SubtotalData[]) => void;
    dateSubtotalData: PRD3031_R01DateSubtotalData[];
    setDateSubtotalData: (data: PRD3031_R01DateSubtotalData[]) => void;
    gridRowData: PRD3031_R01GridRowData[];
    setGridRowData: (data: PRD3031_R01GridRowData[]) => void;

    // === 전체 초기화 ===
    initData: () => void;
}

const defaultDateRange: DateRange = getCurrentMonthDateRange();

export const defaultPRD3031_R01Filter: PRD3031_R01Filter = {
    compCd: '',
    productDateRange: defaultDateRange,
    orderType: '',
    workplaceCd: '',
    itemCd: '',
    itemNm: '',
    brandCd: '',
};

export const usePRD3031_R01Store = create<Store_PRD3031_R01>((set) => {
    const createFilterSetter = (filterKey: keyof Pick<Store_PRD3031_R01, 'PRD3031_R01Filter' | 'PRD3031_R01PastFilter'>) => 
        (filter: Partial<PRD3031_R01Filter>) => set((state) => ({
            ...state,
            [filterKey]: { ...state[filterKey], ...filter }
        }));

    const removeItemsFromArray = <T>(array: T[], itemsToRemove: T[]): T[] => {
        return array.filter(item => !itemsToRemove.includes(item));
    };

    return {
        PRD3031_R01Filter: defaultPRD3031_R01Filter,
        PRD3031_R01PastFilter: defaultPRD3031_R01Filter,
        setPRD3031_R01Filter: createFilterSetter('PRD3031_R01Filter'),
        setPRD3031_R01PastFilter: createFilterSetter('PRD3031_R01PastFilter'),

        PRD3031_R01List: [],
        setPRD3031_R01List: (PRD3031_R01List: ExtendedPRD3031_R01Dto[]) => set({ PRD3031_R01List }),
        insertPRD3031_R01List: (data: ExtendedPRD3031_R01Dto[], index?: number) => set((state) => {
            const _PRD3031_R01List = [...state.PRD3031_R01List];
            
            if (index !== undefined) {
                _PRD3031_R01List.splice(index, 0, ...data);
            } else {
                _PRD3031_R01List.push(...data);
            }
            return { ...state, PRD3031_R01List: _PRD3031_R01List };
        }),
        removePRD3031_R01List: (deletedIndex: number) => set((state) => ({
            ...state,
            PRD3031_R01List: state.PRD3031_R01List.filter((_, index) => index !== deletedIndex),
        })),
        updatePRD3031_R01List: (data: ExtendedPRD3031_R01Dto, index: number) => set((state) => {
            const _PRD3031_R01List = [...state.PRD3031_R01List];
            _PRD3031_R01List[index] = data;
            return { ...state, PRD3031_R01List: _PRD3031_R01List };
        }),

        selectedPRD3031_R01List: [],
        setSelectedPRD3031_R01List: (idList: string[]) => set({ selectedPRD3031_R01List: idList }),
        addSelectedPRD3031_R01List: (idList: string[]) => set((state) => {
            const _selectedPRD3031_R01List = [...state.selectedPRD3031_R01List];
            _selectedPRD3031_R01List.push(...idList.filter(value => !_selectedPRD3031_R01List.includes(value)));
            return { ...state, selectedPRD3031_R01List: _selectedPRD3031_R01List };
        }),
        removeSelectedPRD3031_R01List: (idList?: string[]) => set((state) => {
            if (!idList) {
                return { ...state, selectedPRD3031_R01List: [] };
            }
            
            return {
                ...state,
                selectedPRD3031_R01List: removeItemsFromArray(state.selectedPRD3031_R01List, idList),
            };
        }),
        initSelectedPRD3031_R01List: () => set({ selectedPRD3031_R01List: [] }),
        
        columnState: null,
        setColumnState: (state) => set({ columnState: state }),

        scrollTop: null,
        setScrollTop: (top: number | null) => set({ scrollTop: top }),

        allCount: 0,
        setAllCount: (allCount: number) => set({ allCount }),

        showSubtotals: false,
        setShowSubtotals: (show: boolean) => set({ showSubtotals: show }),
        subtotalData: [],
        setSubtotalData: (data: PRD3031_R01SubtotalData[]) => set({ subtotalData: data }),
        dateSubtotalData: [],
        setDateSubtotalData: (data: PRD3031_R01DateSubtotalData[]) => set({ dateSubtotalData: data }),
        gridRowData: [],
        setGridRowData: (data: PRD3031_R01GridRowData[]) => set({ gridRowData: data }),

        initData: () => set(() => ({
            PRD3031_R01Filter: defaultPRD3031_R01Filter,
            PRD3031_R01PastFilter: defaultPRD3031_R01Filter,
            PRD3031_R01List: [],
            selectedPRD3031_R01List: [],
            columnState: null,
            scrollTop: null,
            allCount: 0,
            showSubtotals: false,
            subtotalData: [],
            dateSubtotalData: [],
            gridRowData: [],
        })),
    };
});
